import { useState, useEffect, useCallback } from 'react';
import { Search, Archive, Trash2, Edit2, Info, Loader2, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  fetchGlobalCategories,
  fetchCategoryStats,
  createGlobalCategory,
  updateGlobalCategory,
  deleteGlobalCategory,
} from '@/lib/data-service';
import type { GlobalCategory, CategoryStats } from '@/types/category.types';
import type { CostCategoryType } from '@/types/budget.types';
import { useCompany } from '@/contexts/CompanyContext';
import { CategoryFormDialog } from '@/components/settings/CategoryFormDialog';

interface CategoryManagementProps {
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
}

export default function CategoryManagement({ searchTerm: parentSearchTerm, setSearchTerm: parentSetSearchTerm }: CategoryManagementProps = {}) {
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  
  // Use parent state if provided, otherwise use local state
  const searchTerm = parentSearchTerm !== undefined ? parentSearchTerm : localSearchTerm;
  const setSearchTerm = parentSetSearchTerm !== undefined ? parentSetSearchTerm : setLocalSearchTerm;
  const [selectedCategory, setSelectedCategory] = useState<GlobalCategory | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [categories, setCategories] = useState<GlobalCategory[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentCompany } = useCompany();

  // Form dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<GlobalCategory | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [categoriesData, statsData] = await Promise.all([
        fetchGlobalCategories(),
        fetchCategoryStats(),
      ]);
      setCategories(categoriesData);
      setCategoryStats(statsData);
    } catch (error) {
      console.error('Failed to load category data:', error);
      toast.error('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [currentCompany?.id, loadData]);

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowFormDialog(true);
  };

  const handleEditCategory = (category: GlobalCategory) => {
    setEditingCategory(category);
    setShowFormDialog(true);
  };

  const handleSaveCategory = async (data: {
    name: string;
    code?: string;
    type: CostCategoryType;
    description?: string;
    isDefault: boolean;
  }) => {
    if (editingCategory) {
      await updateGlobalCategory(editingCategory.id, {
        id: editingCategory.id,
        name: data.name,
        code: data.code,
        type: data.type,
        description: data.description,
        status: editingCategory.status,
        isDefault: data.isDefault,
        usageCount: editingCategory.usageCount,
      });
      toast.success(`Category "${data.name}" updated successfully`);
    } else {
      await createGlobalCategory({
        name: data.name,
        code: data.code,
        type: data.type,
        description: data.description,
        isDefault: data.isDefault,
      });
      toast.success(`Category "${data.name}" created successfully`);
    }
    await loadData();
  };

  const handleDeleteCategory = (category: GlobalCategory) => {
    setSelectedCategory(category);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedCategory) return;
    
    setIsDeleting(true);
    try {
      await deleteGlobalCategory(selectedCategory.id);
      toast.success(`Category "${selectedCategory.name}" deleted successfully`);
      await loadData();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setSelectedCategory(null);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6 pb-32">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Budget Category Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage global budget categories used across all projects
          </p>
        </div>
        <Button onClick={handleAddCategory}>
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : categoryStats?.totalGlobal ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success-600">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : categoryStats?.totalActive ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Archived
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-neutral-500">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : categoryStats?.totalArchived ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Most Used
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : categoryStats?.mostUsedCategories[0]?.name ?? '-'}
            </div>
            <div className="text-xs text-muted-foreground">
              {!isLoading && categoryStats?.mostUsedCategories[0]?.usageCount !== undefined 
                ? `${categoryStats.mostUsedCategories[0].usageCount} projects` 
                : ''}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="border-info-200 bg-info-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-info-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-info-900">
                Global Categories vs Project-Specific Categories
              </p>
              <p className="text-sm text-info-800">
                <strong>Global categories</strong> are managed here and available across all projects (read-only in budget editing).
                Projects can also create <strong>project-specific categories</strong> for unique requirements (editable in budget editing).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
          <CardDescription>
            Manage your organisation's budget category taxonomy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Categories List */}
          <div className="space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredCategories.map((category) => {
              return (
                <div key={category.id} className="border rounded-lg">
                  <div className="p-4 flex items-center gap-3 hover:bg-muted/50">
                    <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-6">
                        <div className="font-medium">{category.name}</div>
                        {category.code && (
                          <div className="text-xs text-muted-foreground">{category.code}</div>
                        )}
                      </div>
                      <div className="col-span-3 text-sm text-muted-foreground">
                        {category.usageCount} projects
                      </div>
                      <div className="col-span-2">
                        {category.isDefault && (
                          <Badge variant="outline" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-1 flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategory(category)}
                          disabled={category.usageCount > 0}
                        >
                          {category.usageCount > 0 ? (
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredCategories.length === 0 && !isLoading && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No categories found matching your search.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Category Dialog */}
      <CategoryFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedCategory?.usageCount === 0 ? 'Delete Category' : 'Archive Category'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCategory?.usageCount === 0 ? (
                <>
                  Are you sure you want to delete <strong>{selectedCategory?.name}</strong>?
                  This action cannot be undone.
                </>
              ) : (
                <>
                  <strong>{selectedCategory?.name}</strong> is currently used in{' '}
                  {selectedCategory?.usageCount} project(s). You cannot delete it, but you can
                  archive it to prevent it from being used in new projects.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : selectedCategory?.usageCount === 0 ? (
                'Delete'
              ) : (
                'Archive'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
